import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, Nav, Tab, Table, Badge, Spinner, Alert, Button, Row, Col, Form } from 'react-bootstrap';
import { dsoApi } from '../../services/api';
import { 
  FaArrowLeft, 
  FaSpinner, 
  FaExclamationTriangle, 
  FaFileInvoice, 
  FaListAlt, 
  FaEye,
  FaFileInvoiceDollar,
  FaCheckSquare
} from 'react-icons/fa';

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return dateString; // Fallback
  }
};

// Helper function to format currency
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return 'N/A';
  return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

function PayoutDetails() {
  const { payoutId } = useParams();
  const [payoutDetails, setPayoutDetails] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [cases, setCases] = useState([]);
  const [selectedCaseIds, setSelectedCaseIds] = useState([]);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [invoiceSuccess, setInvoiceSuccess] = useState(false);
  const [loading, setLoading] = useState({
    details: true,
    invoices: true,
    cases: false,
    createInvoice: false
  });
  const [error, setError] = useState({
    details: '',
    invoices: '',
    cases: '',
    createInvoice: ''
  });
  const [activeTab, setActiveTab] = useState('cases');
  const [casePage, setCasePage] = useState(0);
  const caseLimit = 10;

  // Fetch payout details
  useEffect(() => {
    const fetchPayoutDetails = async () => {
      setLoading(prev => ({ ...prev, details: true }));
      setError(prev => ({ ...prev, details: '' }));
      
      try {
        const response = await dsoApi.getPayoutDetails(payoutId);
        setPayoutDetails(response.data);
      } catch (err) {
        console.error("Error fetching payout details:", err);
        setError(prev => ({ ...prev, details: 'Failed to load payout details.' }));
      } finally {
        setLoading(prev => ({ ...prev, details: false }));
      }
    };

    fetchPayoutDetails();
  }, [payoutId]);


  // Fetch invoices when tab is active or component loads
  useEffect(() => {
    const fetchInvoices = async () => {
      if (activeTab !== 'invoices' && !loading.details) return;
      
      setLoading(prev => ({ ...prev, invoices: true }));
      setError(prev => ({ ...prev, invoices: '' }));
      
      try {
        const response = await dsoApi.getPayoutInvoices(payoutId);
        setInvoices(response.data.items || []);
      } catch (err) {
        console.error("Error fetching payout invoices:", err);
        setError(prev => ({ ...prev, invoices: 'Failed to load invoices.' }));
      } finally {
        setLoading(prev => ({ ...prev, invoices: false }));
      }
    };

    fetchInvoices();
  }, [payoutId, activeTab, loading.details]);

  // Fetch cases when tab is active
  useEffect(() => {
    const fetchCases = async () => {
      if (activeTab !== 'cases') return;
      
      setLoading(prev => ({ ...prev, cases: true }));
      setError(prev => ({ ...prev, cases: '' }));
      
      try {
        const response = await dsoApi.getPayoutCases(
          payoutId,
          {
            skip: casePage * caseLimit,
            limit: caseLimit
          }
        );
        
        setCases(response.data.items || []);
      } catch (err) {
        console.error("Error fetching payout cases:", err);
        setError(prev => ({ ...prev, cases: 'Failed to load cases.' }));
      } finally {
        setLoading(prev => ({ ...prev, cases: false }));
      }
    };

    fetchCases();
  }, [payoutId, activeTab, casePage]);

  // Helper function to map payout case status to string
  const getPayoutCaseStatusString = (statusCode) => {
    switch (statusCode) {
      case 1:
        return 'Requested';
      case 2:
        return 'Approved';
      case 3:
        return 'Disbursed';
      case 4:
        return 'Rejected';
      default:
        return 'Unknown';
    }
  };
  
  // Handle case selection
  const handleCaseSelection = (caseId) => {
    setSelectedCaseIds(prev => {
      if (prev.includes(caseId)) {
        return prev.filter(id => id !== caseId);
      } else {
        return [...prev, caseId];
      }
    });
  };
  
  // Select/deselect all cases
  const handleSelectAllCases = (selected) => {
    if (selected) {
      const allCaseIds = cases.map(caseItem => caseItem.id);
      setSelectedCaseIds(allCaseIds);
    } else {
      setSelectedCaseIds([]);
    }
  };
  
  // Create invoice with selected cases
  const handleCreateInvoice = async () => {
    if (selectedCaseIds.length === 0) {
      setError(prev => ({ ...prev, createInvoice: 'Please select at least one case to create an invoice.' }));
      return;
    }
    
    setError(prev => ({ ...prev, createInvoice: '' }));
    setLoading(prev => ({ ...prev, createInvoice: true }));
    setCreatingInvoice(true);
    
    try {
      // Call API to create invoice
      await dsoApi.createInvoice({
        payout_id: payoutId,
        case_ids: selectedCaseIds
      });
      
      // Success
      setInvoiceSuccess(true);
      setSelectedCaseIds([]);
      
      // Refresh the invoices list
      const response = await dsoApi.getPayoutInvoices(payoutId);
      setInvoices(response.data.items || []);
      
      // Switch to invoices tab
      setActiveTab('invoices');
    } catch (err) {
      console.error("Error creating invoice:", err);
      setError(prev => ({ 
        ...prev, 
        createInvoice: err.response?.data?.detail || 'Failed to create invoice. Please try again.' 
      }));
    } finally {
      setLoading(prev => ({ ...prev, createInvoice: false }));
      setTimeout(() => {
        setCreatingInvoice(false);
        setInvoiceSuccess(false);
      }, 3000);
    }
  };

  // Function to render status badge
  const renderStatusBadge = (status) => {
    let badgeClass = '';
    switch (status?.toLowerCase()) {
      case 'disbursed':
        badgeClass = 'success';
        break;
      case 'approved':
        badgeClass = 'primary';
        break;
      case 'requested':
        badgeClass = 'warning';
        break;
      case 'rejected':
        badgeClass = 'danger';
        break;
      default:
        badgeClass = 'secondary';
    }
    return <Badge bg={badgeClass}>{status || 'Unknown'}</Badge>;
  };

  return (
    <div className="dso-payout-details-page">
      <div className="page-header">
        <Link to="/dso/payouts" className="back-link">
          <FaArrowLeft /> Back to Payouts
        </Link>
        <h2>Payout Details</h2>
      </div>

      {loading.details ? (
        <div className="loading-state">
          <Spinner animation="border" role="status" variant="primary" />
          <span>Loading payout details...</span>
        </div>
      ) : error.details ? (
        <Alert variant="danger">
          <FaExclamationTriangle className="me-2" />
          {error.details}
        </Alert>
      ) : payoutDetails ? (
        <>
          {/* Payout Summary Card */}
          <Card className="mb-4 detail-card">
            <Card.Header className="detail-card-header">
              <h4 className="mb-0">Payout Summary</h4>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6} lg={3} className="detail-item">
                  <div className="detail-label">Requester Name</div>
                  <div className="detail-value">{payoutDetails.payment_requester_name}</div>
                </Col>
                <Col md={6} lg={3} className="detail-item">
                  <div className="detail-label">Total Amount</div>
                  <div className="detail-value">{formatCurrency(payoutDetails.total_amount)}</div>
                </Col>
                <Col md={6} lg={3} className="detail-item">
                  <div className="detail-label">Status</div>
                  <div className="detail-value">{renderStatusBadge(payoutDetails.status_str)}</div>
                </Col>
                <Col md={6} lg={3} className="detail-item">
                  <div className="detail-label">Cases Count</div>
                  <div className="detail-value">{payoutDetails.case_count || 'N/A'}</div>
                </Col>
                <Col md={6} lg={3} className="detail-item">
                  <div className="detail-label">Requested On</div>
                  <div className="detail-value">{formatDate(payoutDetails.requested_at)}</div>
                </Col>
                <Col md={6} lg={3} className="detail-item">
                  <div className="detail-label">Approved On</div>
                  <div className="detail-value">{formatDate(payoutDetails.approved_at)}</div>
                </Col>
                <Col md={6} lg={3} className="detail-item">
                  <div className="detail-label">Disbursed On</div>
                  <div className="detail-value">{formatDate(payoutDetails.disbursed_at)}</div>
                </Col>
                <Col md={6} lg={3} className="detail-item">
                  <div className="detail-label">Approver</div>
                  <div className="detail-value">{payoutDetails.payment_approver_name || 'Not Approved Yet'}</div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Tabs for Invoices and Cases */}
          <Card className="detail-card">
            <Card.Header className="tab-header">
              <Nav variant="tabs">
                <Nav.Item>
                  <Nav.Link 
                    eventKey="cases" 
                    active={activeTab === 'cases'} 
                    onClick={() => setActiveTab('cases')}
                    className="tab-link"
                  >
                    <FaListAlt className="me-2" /> Cases
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link 
                    eventKey="invoices" 
                    active={activeTab === 'invoices'} 
                    onClick={() => setActiveTab('invoices')}
                    className="tab-link"
                  >
                    <FaFileInvoice className="me-2" /> Invoices
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Card.Header>
            <Card.Body className="tab-content-wrapper">
              {/* Invoices Tab */}
              {activeTab === 'invoices' && (
                <>
                  {loading.invoices ? (
                    <div className="tab-loading">
                      <Spinner animation="border" size="sm" variant="primary" />
                      <span>Loading invoices...</span>
                    </div>
                  ) : error.invoices ? (
                    <Alert variant="danger" className="mb-0">
                      <FaExclamationTriangle className="me-2" />
                      {error.invoices}
                    </Alert>
                  ) : invoices.length > 0 ? (
                    <div className="table-responsive">
                      <Table striped hover className="detail-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Invoice No.</th>
                            <th>Amount</th>
                            <th>Status</th>
                            <th>Generated By</th>
                            <th>Generated On</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoices.map((invoice, index) => (
                            <tr key={invoice.id}>
                              <td>{index + 1}</td>
                              <td>
                                <Link 
                                  to={`/dso/invoices/${invoice.id}/details`}
                                  className="link-primary"
                                >
                                  {invoice.invoice_no}
                                </Link>
                              </td>
                              <td>{formatCurrency(invoice.amount)}</td>
                              <td>{renderStatusBadge(invoice.payment_status_str)}</td>
                              <td>{invoice.generated_by_name}</td>
                              <td>{formatDate(invoice.created_at)}</td>
                              <td>
                                <Link
                                  to={`/dso/invoices/${invoice.id}/details`}
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  <FaEye className="me-1" /> View Invoice
                                </Link>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <div className="tab-empty">No invoices found for this payout.</div>
                  )}
                </>
              )}

              {/* Cases Tab */}
              {activeTab === 'cases' && (
                <>
                  {loading.cases ? (
                    <div className="tab-loading">
                      <Spinner animation="border" size="sm" variant="primary" />
                      <span>Loading cases...</span>
                    </div>
                  ) : error.cases ? (
                    <Alert variant="danger" className="mb-0">
                      <FaExclamationTriangle className="me-2" />
                      {error.cases}
                    </Alert>
                  ) : cases.length > 0 ? (
                    <>
                      {/* --- Conditionally show Create Invoice section only for DSO's own payouts --- */}
                      {/* Assumes payoutDetails.payout_type === 'dso' indicates a payout for the DSO themselves */}
                      {payoutDetails.payout_type === 1 && ( // Adjust condition based on actual field
                        <>
                          <div className="d-flex justify-content-between mb-3 align-items-center">
                            <Form.Check
                              type="checkbox"
                              id="select-all-cases"
                              label={`Select All Cases (${cases.length})`}
                              checked={selectedCaseIds.length === cases.length && cases.length > 0}
                              onChange={(e) => handleSelectAllCases(e.target.checked)}
                              className="fw-bold"
                            />
                            
                            {invoices.length === 0 && ( // Only show button if no invoice exists yet
                              <Button 
                                variant="primary" 
                                size="sm" 
                                onClick={handleCreateInvoice}
                                disabled={selectedCaseIds.length === 0 || loading.createInvoice}
                                className="create-invoice-btn"
                              >
                                {loading.createInvoice ? (
                                  <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Creating Invoice...
                                  </>
                                ) : (
                                  <>
                                    <FaFileInvoiceDollar className="me-2" />
                                    Create Invoice
                                  </>
                                )}
                              </Button>
                            )}
                            
                            {invoices.length > 0 && (
                              <Badge bg="info" className="invoice-exists-badge">
                                <FaFileInvoiceDollar className="me-1" />
                                Invoice Already Generated
                              </Badge>
                            )}
                          </div>
                          
                          {error.createInvoice && (
                            <Alert variant="danger" className="mt-2 mb-3">
                              <FaExclamationTriangle className="me-2" />
                              {error.createInvoice}
                            </Alert>
                          )}
                          
                          {invoiceSuccess && (
                            <Alert variant="success" className="mt-2 mb-3">
                              <FaCheckSquare className="me-2" />
                              Invoice created successfully! Redirecting to invoices tab...
                            </Alert>
                          )}
                        </>
                      )}
                      {/* --- End of conditional section --- */}

                      <div className="table-responsive">
                        <Table striped hover className="detail-table">
                          <thead>
                            <tr>
                              {/* Conditionally show Select column only for DSO payouts */}
                              {payoutDetails.payout_type === 1 && <th>Select</th>}
                              <th>#</th>
                              <th>Lead Name</th>
                              <th>Application No.</th>
                              <th>LRN No.</th>
                              <th>Product</th>
                              <th>Case Type</th>
                              <th>Status</th>
                              <th>Payment Approval</th>
                              <th>Created On</th>
                            </tr>
                          </thead>
                          <tbody>
                            {cases.map((caseItem, index) => (
                              <tr key={caseItem.id} className={selectedCaseIds.includes(caseItem.id) ? 'selected-row' : ''}>
                                {/* Conditionally show checkbox only for DSO payouts */}
                                {payoutDetails.payout_type === 1 && (
                                  <td>
                                    <Form.Check
                                      type="checkbox"
                                      id={`case-${caseItem.id}`}
                                      checked={selectedCaseIds.includes(caseItem.id)}
                                      onChange={() => handleCaseSelection(caseItem.id)}
                                    />
                                  </td>
                                )}
                                <td>{casePage * caseLimit + index + 1}</td>
                                <td>{caseItem.lead_name}</td>
                                <td>{caseItem.application_no || 'N/A'}</td>
                                <td>{caseItem.lrn_no || 'N/A'}</td>
                                <td>{caseItem.product_name}</td>
                                <td>{caseItem.case_type}</td>
                                <td>{renderStatusBadge(caseItem.case_status_str)}</td>
                                <td>{renderStatusBadge(getPayoutCaseStatusString(caseItem.payout_case_status))}</td>
                                <td>{formatDate(caseItem.created_at)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>

                        <div className="pagination-controls d-flex justify-content-between align-items-center mt-3">
                          <Button 
                            variant="outline-primary" 
                            size="sm"
                            disabled={casePage === 0}
                            onClick={() => setCasePage(prev => Math.max(0, prev - 1))}
                          >
                            Previous Page
                          </Button>
                          <span className="page-info">Page {casePage + 1}</span>
                          <Button 
                            variant="outline-primary" 
                            size="sm"
                            disabled={cases.length < caseLimit}
                            onClick={() => setCasePage(prev => prev + 1)}
                          >
                            Next Page
                          </Button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="tab-empty">No cases found for this payout.</div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </>
      ) : (
        <Alert variant="warning">Payout not found or you don't have permission to view it.</Alert>
      )}

      <style jsx>{`
        .dso-payout-details-page {
          padding: 20px;
          background-color: #f8fafc;
          max-width: 100%;
          overflow-x: hidden;
        }
        
        .page-header {
          display: flex;
          flex-direction: column;
          margin-bottom: 20px;
        }
        
        .back-link {
          color: #4b5563;
          text-decoration: none;
          margin-bottom: 8px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 0.9rem;
        }
        
        .back-link:hover {
          color: #1f2937;
        }
        
        .page-header h2 {
          margin: 0;
          font-size: 1.4rem;
          font-weight: 600;
          color: #111827;
        }

        .detail-card {
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          border: none;
          border-radius: 8px;
        }
        
        .detail-card-header {
          background-color: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          padding: 12px 16px;
        }
        
        .detail-card-header h4 {
          font-size: 1.1rem;
          font-weight: 600;
          color: #1e293b;
        }
        
        .detail-item {
          margin-bottom: 16px;
        }
        
        .detail-label {
          font-size: 0.8rem;
          color: #6b7280;
          margin-bottom: 4px;
        }
        
        .detail-value {
          font-size: 0.95rem;
          font-weight: 500;
          color: #111827;
        }

        .tab-header {
          background-color: #fff;
          padding: 0;
          border-bottom: none;
        }
        
        .tab-link {
          color: #4b5563;
          border: none;
          border-bottom: 2px solid transparent;
          border-radius: 0;
          padding: 12px 16px;
          font-weight: 500;
          font-size: 0.9rem;
          transition: all 0.2s ease;
        }
        
        .tab-link:hover {
          color: #1e40af;
          background-color: rgba(59, 130, 246, 0.05);
        }
        
        .tab-link.active {
          color: #1e40af;
          border-bottom-color: #1e40af;
          background-color: transparent;
        }
        
        .tab-content-wrapper {
          padding: 16px;
        }
        
        .loading-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px;
          gap: 16px;
          background-color: #ffffff;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          color: #6b7280;
        }
        
        .tab-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          gap: 10px;
          color: #6b7280;
        }
        
        .tab-empty {
          padding: 24px;
          text-align: center;
          color: #6b7280;
          font-size: 0.9rem;
          background-color: #f9fafb;
          border-radius: 6px;
        }
        
        .table-responsive {
          overflow-x: auto;
          margin: 0 -16px;
          padding: 0 16px;
        }
        
        .detail-table {
          font-size: 0.85rem;
        }
        
        .detail-table th {
          font-weight: 600;
          color: #4b5563;
          background-color: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
          white-space: nowrap;
          padding: 10px 16px;
        }
        
        .detail-table td {
          padding: 12px 16px;
          vertical-align: middle;
          border-bottom: 1px solid #f1f5f9;
        }
        
        .link-primary {
          color: #2563eb;
          text-decoration: none;
          font-weight: 500;
        }
        
        .link-primary:hover {
          text-decoration: underline;
          color: #1d4ed8;
        }
        
        .pagination-controls {
          margin-top: 16px;
        }

        .page-info {
          font-size: 0.9rem;
          color: #6b7280;
        }
        
        /* Responsive improvements */
        @media (min-width: 768px) {
          .page-header {
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
          }
          
          .back-link {
            margin-bottom: 0;
          }
        }
        
        /* Selected row styling */
        .selected-row {
          background-color: rgba(13, 110, 253, 0.05) !important; 
        }
        
        /* Create Invoice button */
        .create-invoice-btn {
          background-color: #4f46e5;
          border-color: #4f46e5;
          display: inline-flex;
          align-items: center;
        }
        
        .create-invoice-btn:hover:not(:disabled) {
          background-color: #4338ca;
          border-color: #4338ca;
        }
        
        .create-invoice-btn:disabled {
          background-color: #c7d2fe;
          border-color: #c7d2fe;
        }
        
        .invoice-exists-badge {
          display: inline-flex;
          align-items: center;
          font-size: 0.85rem;
          padding: 8px 12px;
          background-color: #60a5fa;
        }
      `}</style>
    </div>
  );
}

export default PayoutDetails;
